import { derived, get, writable } from "svelte/store";
import { PolygonSelectLayerManager } from "./polygon-select/PolygonSelectLayerManager";
import { ShortCutClassicLayer } from "../../map/layers/ShortCutClassicLayer";
import { appManager } from "../../AppManager";
import * as turf from "@turf/turf";
import "uuid";
import { LayersManager } from "./LayersManager";
import { VehiclesManager } from "./VehiclesManager";
import { MapManager } from "../../lib/maplibre/MapManager";
export class TripsModalManager {
    constructor(modalHook) {
        this.open = writable(false);
        this.selection = writable(undefined);
        this.matrix = writable(undefined);
        this.layers = [];
        this.vehicles = [
            {
                id: 1,
                name: "Car",
                logo: "car",
            },
            {
                id: 2,
                name: "Bicycle",
                logo: "bicycle",
            },
        ];
        this.layersWhiteList = [
            "16883969-a552-460d-896d-480fab0aef0d",
            "625d7487-abbb-4dd6-ac72-f57468ca497f",
            "de7bd9f7-76bb-4da1-83d7-e8fc04547cd7"
        ];
        this.vehicleTypeColors = ["#FFFFFF", "#FF0000", "#0000FF"];
        this.mapContainerStore = writable(undefined);
        this.destinationTrafficStore = writable(true);
        this.throughTrafficStore = writable(true);
        /**
         * When higher zoom, interactivity is possible.
         */
        this.ZOOM_OUT_VIEW_LEVEL = 8;
        /**
         * When higher zoom, data is visible.
         */
        this.MIN_DATA_ZOOM = 6;
        this.modalHook = modalHook;
        this.modalHook.on("open", async () => {
            this.open.set(true);
            await this.initFirstOpen();
        });
        this.modalHook.on("close", () => {
            this.open.set(false);
        });
        this.mapContainerStore.subscribe(async (c) => {
            if (typeof c === "undefined")
                return;
            await this.initFirstOpen();
        });
        this.selection.subscribe(selection => {
            this.updateSelectedTrips();
        });
        this.destinationTrafficStore.subscribe(() => {
            this.updateSelectedTrips();
        });
        this.throughTrafficStore.subscribe(() => {
            this.updateSelectedTrips();
        });
        this.vehiclesManager = new VehiclesManager(this);
        this.vehiclesManager.getVisbleVehiclesStore().subscribe(async () => {
            await this.updateSelectedTrips();
        });
        this.layersManager = new LayersManager(this, this.vehiclesManager.getVisbleVehiclesStore());
        this.layersManager.getVisibleLayersStore().subscribe(async () => {
            await this.updateSelectedTrips();
        });
        this.canSave = derived(this.matrix, (m) => {
            return typeof m !== "undefined";
        });
    }
    close() {
        this.modalHook.close(undefined);
    }
    save() {
        this.modalHook.close(get(this.matrix));
    }
    getOpenStore() {
        return this.open;
    }
    getLayersManager() {
        return this.layersManager;
    }
    getVehicles() {
        return this.vehicles;
    }
    getDestinationTrafficStore() {
        return this.destinationTrafficStore;
    }
    getThroughTrafficStore() {
        return this.throughTrafficStore;
    }
    getVehiclesManager() {
        return this.vehiclesManager;
    }
    getCanSaveStore() {
        return this.canSave;
    }
    getMapContainerStore() {
        return this.mapContainerStore;
    }
    async initFirstOpen() {
        const mapContainer = get(this.mapContainerStore);
        if (!get(this.open))
            return;
        if (typeof mapContainer === "undefined")
            return;
        const modalData = this.modalHook.getData();
        const models = await appManager.api.trips.getModels({
            accessToken: await appManager.api.getAccessToken()
        });
        this.layers.length = 0;
        //console.log(models);
        const centerPoint = turf.point([modalData.center.lng, modalData.center.lat]);
        models.forEach(m => {
            if (typeof m.extent !== "undefined" && m.extent !== null) {
                let extentPolygon;
                if (m.extent.type === "Polygon") {
                    extentPolygon = turf.polygon(m.extent.coordinates);
                }
                else if (m.extent.type === "MultiPolygon") {
                    extentPolygon = turf.multiPolygon(m.extent.coordinates);
                }
                //console.log(m);
                //console.log(extentPolygon);
                if (typeof extentPolygon !== "undefined") {
                    if (!turf.booleanPointInPolygon(centerPoint, extentPolygon))
                        return;
                }
            }
            this.layers.push(...m.layers.filter(l => {
                return this.layersWhiteList.indexOf(l.globalId) !== -1;
            }).map(l => {
                return {
                    layer: l,
                    visible: l.properties?.show !== "false",
                    mvt: this.MvtUrlForLayer(l),
                    vehicle: this.vehicles.find((x) => x.id == l.vehicleType),
                    selected: l.properties?.show !== "false",
                };
            }));
        });
        //console.log(this.layers);
        const mapOptions = {
            imageryLayers: [],
            baseLayer: new ShortCutClassicLayer(),
        };
        this.mapManager = new MapManager(mapOptions);
        const polygonSelectLayer = new PolygonSelectLayerManager(this.mapManager, true, {
            minZoom: this.ZOOM_OUT_VIEW_LEVEL,
        });
        await this.mapManager.initialize(mapContainer, async () => { return; }, modalData);
        polygonSelectLayer.getSelectionStore().subscribe(selection => {
            this.selection.set(selection);
        });
        // initialize sub components.
        await this.layersManager.initialize(this.layers);
    }
    MvtUrlForLayer(layer) {
        return appManager.api.trips.url + `layers/${layer.id}/tiles/mvt.json`;
    }
    async updateSelectedTrips() {
        if (typeof this.mapManager === "undefined")
            return;
        if (!this.mapManager.loaded())
            return;
        this.addLayerLayers();
        if (this.mapManager.hasSource("od-points")) {
            this.mapManager.getSource("od-points").setData(turf.featureCollection([]));
        }
        const selection = get(this.selection);
        if (typeof selection === "undefined") {
            this.matrix.set(undefined);
            return;
        }
        if (typeof this.abortController !== "undefined") {
            this.abortController.abort();
        }
        this.abortController = new AbortController();
        const signal = this.abortController.signal;
        const matrix = {
            name: "Trip Layer Locations",
            locations: [],
            elements: [],
            properties: {
                type: "trip-layer-data"
            }
        };
        const accessToken = await appManager.api.getAccessToken();
        const visibleLayers = get(this.layersManager.getVisibleLayersStore());
        const visibleVehicles = get(this.vehiclesManager.getVisbleVehiclesStore());
        const points = turf.featureCollection([]);
        for (let i = 0; i < this.layers.length; i++) {
            const l = this.layers[i];
            const pointMap = {};
            if (!visibleVehicles.has(l.layer.vehicleType))
                continue;
            if (!visibleLayers.has(l.layer.id))
                continue;
            const matrixResponse = await appManager.api.trips.postMatrix({
                accessToken: accessToken,
                layerId: l.layer.id,
                polygon: selection.geometry,
                signal: signal,
                destinationTraffic: get(this.destinationTrafficStore),
                throughTraffic: get(this.throughTrafficStore),
            });
            if (signal.aborted)
                return;
            // first do all points.
            matrixResponse.features.forEach(f => {
                if (f.geometry.type !== "Point")
                    return;
                const point = f.geometry;
                const id = f.properties["id"];
                if (typeof id === "undefined")
                    return;
                const globalId = matrix.locations.length;
                matrix.locations.push({
                    latitude: f.geometry.coordinates[1],
                    longitude: f.geometry.coordinates[0],
                    properties: {
                        source: "trip-layer",
                        type: "unknown"
                    }
                });
                f.properties["id"] = globalId;
                f.properties["count"] = 0;
                f.properties["count_to"] = 0;
                f.properties["count_from"] = 0;
                points.features.push(f);
                pointMap[id] = f;
            });
            matrixResponse.features.forEach(f => {
                if (f.geometry.type !== "LineString")
                    return;
                const from = f.properties["from_id"];
                const to = f.properties["to_id"];
                const count = f.properties["count"];
                if (count == 0)
                    return;
                const fromPoint = pointMap[from];
                fromPoint.properties["count"] = fromPoint.properties["count"] + count;
                fromPoint.properties["count_from"] = fromPoint.properties["count_from"] + count;
                const toPoint = pointMap[to];
                toPoint.properties["count"] = toPoint.properties["count"] + count;
                toPoint.properties["count_to"] = toPoint.properties["count_to"] + count;
                const fromGlobal = fromPoint.properties["id"];
                const toGlobal = toPoint.properties["id"];
                if (fromGlobal == toGlobal)
                    return;
                matrix.elements.push({
                    count: count,
                    fromIdx: fromGlobal,
                    toIdx: toGlobal
                });
            });
        }
        if (signal.aborted)
            return;
        if (!this.mapManager.getSource("od-points")) {
            this.mapManager.addSource("od-points", {
                type: "geojson",
                data: {
                    type: "FeatureCollection",
                    features: [],
                },
            });
            this.mapManager.addLayer({
                id: "od-points-single-back",
                type: "circle",
                source: "od-points",
                minzoom: this.ZOOM_OUT_VIEW_LEVEL,
                layout: {
                    visibility: "visible",
                },
                paint: {
                    "circle-color": "#000000",
                    "circle-radius": 8,
                },
                filter: ["<=", ["get", "count"], 1],
            }, "label");
            this.mapManager.addLayer({
                id: "od-points-back",
                type: "circle",
                source: "od-points",
                minzoom: this.ZOOM_OUT_VIEW_LEVEL,
                layout: {
                    visibility: "visible",
                },
                paint: {
                    "circle-color": "#000000",
                    "circle-radius": 14,
                },
                filter: [">", ["get", "count"], 1],
            }, "label");
            this.mapManager.addLayer({
                id: "od-points-single",
                type: "circle",
                source: "od-points",
                minzoom: this.ZOOM_OUT_VIEW_LEVEL,
                layout: {
                    visibility: "visible",
                },
                paint: {
                    "circle-color": "#ffc000",
                    "circle-stroke-width": 0,
                    "circle-radius": 6,
                },
                filter: ["<=", ["get", "count"], 1],
            }, "label");
            this.mapManager.addLayer({
                id: "od-points",
                type: "circle",
                source: "od-points",
                minzoom: this.ZOOM_OUT_VIEW_LEVEL,
                layout: {
                    visibility: "visible",
                },
                paint: {
                    "circle-color": "#ffc000",
                    "circle-stroke-width": 0,
                    "circle-radius": 12,
                },
                filter: [">", ["get", "count"], 1],
            }, "label");
            this.mapManager.addLayer({
                id: "od-points-labels",
                type: "symbol",
                source: "od-points",
                minzoom: this.ZOOM_OUT_VIEW_LEVEL,
                layout: {
                    visibility: "visible",
                    "text-field": "{count}",
                    "text-size": 15,
                },
                paint: {
                    "text-halo-color": "#ffffff",
                    "text-halo-width": 2,
                },
                filter: [">", ["get", "count"], 1],
            });
        }
        this.mapManager.getSource("od-points").setData(points);
        this.matrix.set(matrix);
    }
    addLayerLayers() {
        const lowest = "symbol";
        const visibleLayers = get(this.layersManager.getVisibleLayersStore());
        const visibleVehicles = get(this.vehiclesManager.getVisbleVehiclesStore());
        this.layers.forEach((l) => {
            const layer = l.layer;
            const layerName = `trips_layer_${layer.id}`;
            if (typeof this.mapManager.getSource(layerName) === "undefined") {
                this.mapManager.addSource(layerName, {
                    type: "vector",
                    url: l.mvt,
                });
                const shadowSize = 4;
                this.mapManager.addLayer({
                    id: layerName + "-shadow",
                    type: "line",
                    source: layerName,
                    "source-layer": "trips",
                    minzoom: this.ZOOM_OUT_VIEW_LEVEL,
                    layout: {
                        visibility: "visible",
                        "line-cap": "round",
                    },
                    paint: {
                        "line-width": [
                            "interpolate",
                            ["linear"],
                            ["zoom"],
                            8,
                            [
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                /* @ts-ignore */
                                "+",
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                /* @ts-ignore */
                                [
                                    "min",
                                    ["/", ["get", "trip_count"], 1000],
                                    2,
                                ],
                                shadowSize / 2,
                            ],
                            10,
                            [
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                /* @ts-ignore */
                                "+",
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                /* @ts-ignore */
                                [
                                    "min",
                                    ["/", ["get", "trip_count"], 100],
                                    5,
                                ],
                                shadowSize / 2,
                            ],
                            14,
                            [
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                /* @ts-ignore */
                                "+",
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                /* @ts-ignore */
                                [
                                    "max",
                                    [
                                        "min",
                                        ["/", ["get", "trip_count"], 20],
                                        20,
                                    ],
                                    1,
                                ],
                                shadowSize / 2,
                            ],
                        ],
                        "line-blur": shadowSize,
                        "line-color": "#666",
                        "line-opacity": 1,
                        "line-translate": [
                            shadowSize / 1.8,
                            shadowSize / 1.8,
                        ],
                        "line-offset": [
                            "interpolate",
                            ["linear"],
                            ["zoom"],
                            13,
                            0,
                            14,
                            5,
                            15,
                            10,
                        ],
                    },
                }, lowest);
                this.mapManager.addLayer({
                    id: layerName + "-shadow-sla",
                    type: "line",
                    source: layerName,
                    "source-layer": "trips",
                    minzoom: this.MIN_DATA_ZOOM,
                    layout: {
                        visibility: "visible",
                        "line-cap": "round",
                    },
                    paint: {
                        "line-width": 1,
                        "line-blur": shadowSize,
                        "line-color": "#666",
                        "line-opacity": 1,
                        "line-translate": [
                            shadowSize / 1.8,
                            shadowSize / 1.8,
                        ],
                        "line-offset": [
                            "interpolate",
                            ["linear"],
                            ["zoom"],
                            13,
                            0,
                            14,
                            5,
                            15,
                            10,
                        ],
                    },
                    filter: ["any"],
                }, lowest);
            }
        });
        // Second loop adds the layer and symbology
        this.layers.forEach((l) => {
            const layer = l.layer;
            const layerName = `trips_layer_${layer.id}`;
            const visible = visibleVehicles.has(l.layer.vehicleType) && visibleLayers.has(l.layer.id);
            if (this.mapManager.hasLayer(layerName)) {
                this.mapManager.setLayoutProperty(layerName, "visibility", visible ? "visible" : "none");
                this.mapManager.setLayoutProperty(layerName + "-shadow", "visibility", visible ? "visible" : "none");
                this.mapManager.setLayoutProperty(layerName + "-high", "visibility", visible ? "visible" : "none");
                this.mapManager.setLayoutProperty(layerName + "-counts", "visibility", visible ? "visible" : "none");
                this.mapManager.setLayoutProperty(layerName + "-sla", "visibility", "none");
                this.mapManager.setLayoutProperty(layerName + "-counts-sla", "visibility", "none");
                this.mapManager.setLayoutProperty(layerName + "-shadow-sla", "visibility", "none");
            }
            else {
                const color = this.vehicleTypeColors[layer.vehicleType];
                this.mapManager.addLayer({
                    id: layerName,
                    type: "line",
                    source: layerName,
                    "source-layer": "trips",
                    minzoom: this.ZOOM_OUT_VIEW_LEVEL,
                    layout: {
                        visibility: "visible",
                        "line-cap": "round",
                    },
                    paint: {
                        "line-width": [
                            "interpolate",
                            ["linear"],
                            ["zoom"],
                            8,
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            ["min", ["/", ["get", "trip_count"], 1000], 2],
                            10,
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            ["min", ["/", ["get", "trip_count"], 100], 5],
                            14,
                            [
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                /* @ts-ignore */
                                "max",
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                /* @ts-ignore */
                                [
                                    "min",
                                    ["/", ["get", "trip_count"], 20],
                                    20,
                                ],
                                1,
                            ],
                        ],
                        "line-color": color,
                        "line-offset": [
                            "interpolate",
                            ["linear"],
                            ["zoom"],
                            13,
                            0,
                            14,
                            5,
                            15,
                            10,
                        ],
                    },
                }, lowest);
                this.mapManager.addLayer({
                    id: layerName + "-high",
                    type: "line",
                    source: layerName,
                    "source-layer": "trips",
                    maxzoom: this.ZOOM_OUT_VIEW_LEVEL,
                    minzoom: this.MIN_DATA_ZOOM,
                    layout: {
                        visibility: "visible",
                        "line-cap": "round",
                    },
                    paint: {
                        "line-width": [
                            "interpolate",
                            ["linear"],
                            ["zoom"],
                            8,
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            ["min", ["/", ["get", "trip_count"], 1000], 2],
                            10,
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            ["min", ["/", ["get", "trip_count"], 100], 5],
                            14,
                            [
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                /* @ts-ignore */
                                "max",
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                /* @ts-ignore */
                                [
                                    "min",
                                    ["/", ["get", "trip_count"], 20],
                                    20,
                                ],
                                1,
                            ],
                        ],
                        "line-color": color,
                        "line-offset": [
                            "interpolate",
                            ["linear"],
                            ["zoom"],
                            13,
                            0,
                            14,
                            5,
                            15,
                            10,
                        ],
                    },
                    filter: [">", ["get", "trip_count"], 10],
                }, lowest);
                this.mapManager.addLayer({
                    id: layerName + "-counts",
                    type: "symbol",
                    source: layerName,
                    "source-layer": "trips",
                    minzoom: this.ZOOM_OUT_VIEW_LEVEL,
                    layout: {
                        visibility: "visible",
                        "text-field": "{trip_count}",
                        "text-size": 15,
                        "symbol-placement": "line-center",
                        "text-offset": [
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            "interpolate",
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            ["linear"],
                            ["zoom"],
                            12,
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            ["literal", [0, 0]],
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            13,
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            ["literal", [0.25, 0.25]],
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            14,
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            ["literal", [0.5, 0.5]],
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            15,
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            ["literal", [0.75, 0.75]],
                        ],
                    },
                    paint: {
                        "text-color": color,
                        "text-halo-color": "#ffffff",
                        "text-halo-width": 2,
                    },
                }, lowest);
                this.mapManager.addLayer({
                    id: layerName + "-sla",
                    type: "line",
                    source: layerName,
                    "source-layer": "trips",
                    minzoom: this.MIN_DATA_ZOOM,
                    layout: {
                        visibility: "visible",
                        "line-cap": "round",
                    },
                    paint: {
                        "line-width": 1,
                        "line-color": color,
                        "line-offset": [
                            "interpolate",
                            ["linear"],
                            ["zoom"],
                            13,
                            0,
                            14,
                            0,
                            15,
                            10,
                        ],
                    },
                    filter: ["any"],
                }, lowest);
                this.mapManager.addLayer({
                    id: layerName + "-counts-sla",
                    type: "symbol",
                    source: layerName,
                    "source-layer": "trips",
                    minzoom: this.MIN_DATA_ZOOM,
                    layout: {
                        visibility: "visible",
                        "text-field": "",
                        "text-size": 15,
                        "symbol-placement": "line-center",
                        "text-offset": [
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            "interpolate",
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            ["linear"],
                            ["zoom"],
                            12,
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            ["literal", [0, 0]],
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            13,
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            ["literal", [0.25, 0.25]],
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            14,
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            ["literal", [0.5, 0.5]],
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            15,
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            ["literal", [0.75, 0.75]],
                        ],
                    },
                    paint: {
                        "text-color": color,
                        "text-halo-color": "#ffffff",
                        "text-halo-width": 2,
                    },
                    filter: ["any"],
                }, lowest);
                this.mapManager.addLayer({
                    id: layerName + "-locations",
                    type: "circle",
                    source: layerName,
                    "source-layer": "locations",
                    minzoom: this.ZOOM_OUT_VIEW_LEVEL,
                    layout: {
                        visibility: "visible",
                    },
                    paint: {
                        "circle-color": "rgba(255, 255, 255, 1)",
                        "circle-stroke-width": 1,
                        "circle-radius": [
                            "interpolate",
                            ["linear"],
                            ["zoom"],
                            10,
                            1,
                            15,
                            3,
                        ],
                    },
                }, lowest);
            }
        });
    }
}
